/**
 * store/auth.ts
 *
 * Auth state management using cookies (primary) with localStorage fallback.
 * Cookies persist reliably across iOS PWA sessions.
 * localStorage is kept in sync as a fallback.
 *
 * ⚠️  Never store decrypted sensitive data.
 *     Only the JWT (user_id + couple_id) is stored here.
 */
import type { JwtPayload } from '@just-us/shared'

const TOKEN_KEY = 'just-us:token'
const COOKIE_NAME = 'jut_us_t'

// ---------------------------------------------------------------------------
// Cookie helpers
// ---------------------------------------------------------------------------

const setCookie = (token: string, expiresDays = 30): void => {
  const expires = new Date()
  expires.setDate(expires.getDate() + expiresDays)
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(token)};expires=${expires.toUTCString()};path=/;SameSite=Lax`
}

const getCookieToken = (): string | null => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`))
  return match ? decodeURIComponent(match[1]!) : null
}

const clearCookie = (): void => {
  document.cookie = `${COOKIE_NAME}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`
}

// ---------------------------------------------------------------------------
// JWT decode
// ---------------------------------------------------------------------------

const decodeJwtPayload = (token: string): JwtPayload | null => {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const raw = parts[1]!
    const padded = raw
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(raw.length + ((4 - (raw.length % 4)) % 4), '=')
    return JSON.parse(atob(padded)) as JwtPayload
  } catch {
    return null
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const getToken = (): string | null => {
  // Try cookie first (more reliable on iOS PWA)
  const cookieToken = getCookieToken()
  if (cookieToken) return cookieToken
  // Fallback to localStorage
  return localStorage.getItem(TOKEN_KEY)
}

export const setToken = (token: string): void => {
  // Always set both — cookie for persistence, localStorage for fallback
  setCookie(token)
  localStorage.setItem(TOKEN_KEY, token)
}

export const clearToken = (): void => {
  clearCookie()
  localStorage.removeItem(TOKEN_KEY)
}

export const getAuthPayload = (): JwtPayload | null => {
  const token = getToken()
  if (!token) return null
  const payload = decodeJwtPayload(token)
  if (!payload) return null
  // Check expiry
  if (payload.exp < Math.floor(Date.now() / 1000)) {
    clearToken()
    return null
  }
  return payload
}

export const isAuthenticated = (): boolean => getAuthPayload() !== null
